/**
 * Top-level state machine (FS-02 §2.2, §4.3). Transitions are queued and applied at a step
 * boundary (after the substep loop, before render) so handlers never see a half-stepped sim.
 */
export const enum GameState {
  Boot,
  Menu,
  Countdown,
  Playing,
  Paused,
  Dying,
  Results,
  Fault,
}

const LEGAL: Readonly<Record<GameState, readonly GameState[]>> = {
  [GameState.Boot]: [GameState.Menu, GameState.Fault],
  [GameState.Menu]: [GameState.Countdown, GameState.Fault],
  [GameState.Countdown]: [GameState.Playing, GameState.Paused, GameState.Menu],
  [GameState.Playing]: [GameState.Paused, GameState.Dying, GameState.Results, GameState.Fault],
  [GameState.Paused]: [GameState.Countdown, GameState.Results, GameState.Menu],
  [GameState.Dying]: [GameState.Results, GameState.Playing],
  [GameState.Results]: [GameState.Menu, GameState.Countdown],
  [GameState.Fault]: [GameState.Boot],
};

type Cb = (other: GameState) => void;

export interface StateMachine {
  readonly current: GameState;
  readonly ticksInState: number;
  request(to: GameState, reason?: string): void;
  /** Applied at the step boundary; returns true if a transition occurred. */
  applyPending(): boolean;
  simulates(): boolean;
  onEnter(state: GameState, fn: Cb): () => void;
  onExit(state: GameState, fn: Cb): () => void;
  tick(): void;
}

export function createStateMachine(
  initial: GameState,
  onChange: (from: GameState, to: GameState) => void,
): StateMachine {
  let current = initial;
  let ticksInState = 0;
  let pending: GameState | null = null;
  const enterCbs = new Map<GameState, Cb[]>();
  const exitCbs = new Map<GameState, Cb[]>();

  function fire(map: Map<GameState, Cb[]>, state: GameState, other: GameState): void {
    const l = map.get(state);
    if (!l) return;
    for (let i = 0; i < l.length; i++) l[i]!(other);
  }

  return {
    get current() {
      return current;
    },
    get ticksInState() {
      return ticksInState;
    },
    request(to: GameState, reason?: string): void {
      if (to === current) return;
      if (!LEGAL[current].includes(to)) {
        if (import.meta.env.DEV) {
          console.warn(`Illegal transition ${STATE_NAME[current]} → ${STATE_NAME[to]} (${reason ?? ''})`);
        }
        return;
      }
      pending = to;
    },
    applyPending(): boolean {
      if (pending === null) return false;
      const to = pending;
      const from = current;
      pending = null;
      fire(exitCbs, from, to);
      current = to;
      ticksInState = 0;
      fire(enterCbs, to, from);
      onChange(from, to);
      return true;
    },
    simulates(): boolean {
      return current === GameState.Playing || current === GameState.Dying;
    },
    onEnter(state: GameState, fn: Cb): () => void {
      const l = enterCbs.get(state) ?? [];
      l.push(fn);
      enterCbs.set(state, l);
      return () => {
        const i = l.indexOf(fn);
        if (i >= 0) l.splice(i, 1);
      };
    },
    onExit(state: GameState, fn: Cb): () => void {
      const l = exitCbs.get(state) ?? [];
      l.push(fn);
      exitCbs.set(state, l);
      return () => {
        const i = l.indexOf(fn);
        if (i >= 0) l.splice(i, 1);
      };
    },
    tick(): void {
      ticksInState++;
    },
  };
}

/** Human name, for HUD/telemetry. */
export const STATE_NAME: Record<GameState, string> = {
  [GameState.Boot]: 'Boot',
  [GameState.Menu]: 'Menu',
  [GameState.Countdown]: 'Countdown',
  [GameState.Playing]: 'Playing',
  [GameState.Paused]: 'Paused',
  [GameState.Dying]: 'Dying',
  [GameState.Results]: 'Results',
  [GameState.Fault]: 'Fault',
};
